import React, { useState, useEffect, useCallback, useRef } from 'react';
import styled, { keyframes, css } from 'styled-components';
import { useNavigate } from 'react-router-dom';
import LoginPromptModal from '../../components/LoginPromptModal';
import PremiumUpgradeModal from '../../components/PremiumUpgradeModal';
import { useAuth } from '../../contexts/AuthContext';
import { playCorrect, playWrong, playClap } from '../../utils/sounds';

// ─── Theme ────────────────────────────────────────────────────────────────────
const PRIMARY = '#2DBCAF';
const PRIMARY_GR = 'linear-gradient(135deg, #2BBCAF 0%, #1fa99c 50%, #18938a 100%)';
const BG_GR = 'linear-gradient(160deg, #f9e8d9 0%, #faf0e8 40%, #e8f5f3 100%)';
const FONT = "'Plus Jakarta Sans', sans-serif";

// ─── Animations ───────────────────────────────────────────────────────────────
const bounce = keyframes`
  0%, 100% { transform: scale(1); }
  40% { transform: scale(1.15) translateY(-6px); }
  70% { transform: scale(1.07) translateY(-3px); }
`;
const fadeInUp = keyframes`
  from { opacity: 0; transform: translateY(30px); }
  to { opacity: 1; transform: translateY(0); }
`;
const float = keyframes`
  0%, 100% { transform: translateY(0px) rotate(-5deg); }
  50% { transform: translateY(-12px) rotate(5deg); }
`;
const floatDelay = keyframes`
  0%, 100% { transform: translateY(0px) rotate(8deg); }
  50% { transform: translateY(-8px) rotate(-3deg); }
`;
const shakeAnim = keyframes`
  0%, 100% { transform: translateX(0); }
  15% { transform: translateX(-8px); }
  30% { transform: translateX(8px); }
  45% { transform: translateX(-6px); }
  60% { transform: translateX(6px); }
`;
const pulseGlow = keyframes`
  0%, 100% { box-shadow: 0 4px 12px rgba(45,188,175,0.3); }
  50% { box-shadow: 0 4px 24px rgba(45,188,175,0.6); }
`;
const popIn = keyframes`
  0% { transform: scale(0); opacity: 0; }
  70% { transform: scale(1.1); }
  100% { transform: scale(1); opacity: 1; }
`;

// ─── Shapes Data ──────────────────────────────────────────────────────────────
interface ShapeType {
  id: string;
  nameId: string;
  path: string;
  viewBox: string;
}

const SHAPES: ShapeType[] = [
  { id: 'square', nameId: 'Persegi', viewBox: '0 0 100 100', path: 'M15,15 h70 v70 h-70 Z' },
  { id: 'circle', nameId: 'Lingkaran', viewBox: '0 0 100 100', path: 'M50,10 a40,40 0 1,1 0,80 a40,40 0 1,1 0,-80' },
  { id: 'heart', nameId: 'Hati', viewBox: '0 0 100 100', path: 'M50,88 C20,60 10,40 10,28 C10,12 25,5 40,15 C45,18 50,25 50,25 C50,25 55,18 60,15 C75,5 90,12 90,28 C90,40 80,60 50,88 Z' },
  { id: 'triangle', nameId: 'Segitiga', viewBox: '0 0 100 100', path: 'M50,10 L90,90 L10,90 Z' },
  { id: 'diamond', nameId: 'Belah Ketupat', viewBox: '0 0 100 100', path: 'M50,5 L95,50 L50,95 L5,50 Z' },
  { id: 'star', nameId: 'Bintang', viewBox: '0 0 100 100', path: 'M50,5 L61,35 L95,35 L68,55 L79,90 L50,70 L21,90 L32,55 L5,35 L39,35 Z' },
  { id: 'pentagon', nameId: 'Pentagon', viewBox: '0 0 100 100', path: 'M50,5 L95,38 L77,90 L23,90 L5,38 Z' },
];

const COLORS = ['#3B82F6', '#EF4444', '#22C55E', '#F59E0B', '#8B5CF6', '#EC4899', '#06B6D4'];

// ─── Level Config ─────────────────────────────────────────────────────────────
interface LevelConfig {
  shapeCount: number;
  sameColor: boolean;
}

const LEVELS: LevelConfig[] = [
  // Free levels (1-5)
  { shapeCount: 3, sameColor: true },   // Level 1: FREE
  { shapeCount: 4, sameColor: true },   // Level 2: needs login
  { shapeCount: 4, sameColor: false },  // Level 3
  { shapeCount: 5, sameColor: false },  // Level 4
  { shapeCount: 5, sameColor: false },  // Level 5
  // Premium levels (6-10)
  { shapeCount: 6, sameColor: false },  // Level 6: PREMIUM
  { shapeCount: 6, sameColor: false },  // Level 7
  { shapeCount: 7, sameColor: false },  // Level 8
  { shapeCount: 7, sameColor: false },  // Level 9
  { shapeCount: 7, sameColor: false },  // Level 10
];

const MAX_LEVELS = LEVELS.length;
const DEFAULT_PREMIUM_START = 6; // Default: Level 6+ requires premium (index 5)
const DEFAULT_LOGIN_START = 2;   // Default: Level 2+ requires login (index 1)

// ─── Types ────────────────────────────────────────────────────────────────────
interface PlacedShape {
  id: string;
  shapeType: ShapeType;
  color: string;
  x: number;
  y: number;
  pairId: string;
  matched: boolean;
}

interface Connection {
  from: PlacedShape;
  to: PlacedShape;
}

interface DragState {
  active: boolean;
  fromShape: PlacedShape | null;
  startX: number;
  startY: number;
  currentX: number;
  currentY: number;
}

// ─── Intro Styled Components ──────────────────────────────────────────────────
const IntroOverlay = styled.div`
  position: fixed; inset: 0; z-index: 900;
  background: ${BG_GR}; display: flex; flex-direction: column;
  align-items: center; overflow: hidden; font-family: ${FONT};
`;
const IntroWrapper = styled.div`
  max-width: 512px; margin: 0 auto; width: 100%;
  min-height: 100dvh; display: flex; flex-direction: column;
  position: relative;
`;
const TopNav = styled.header`
  position: sticky; top: 0; z-index: 10;
  background: rgba(255,255,255,0.88); backdrop-filter: blur(12px);
  border-bottom: 1px solid rgba(45,188,175,0.1);
  padding: 10px 16px; display: flex; align-items: center;
`;
const BackBtn = styled.button`
  width: 36px; height: 36px; background: transparent; border: none;
  cursor: pointer; display: flex; align-items: center; justify-content: center;
  color: #333; transition: color 0.2s;
  &:hover { color: ${PRIMARY}; }
  .material-symbols-outlined { font-size: 26px; }
`;
const CharArea = styled.div`
  flex: 1; display: flex; align-items: center; justify-content: center;
  position: relative; min-height: 0;
`;
const FloatShape = styled.div<{ $top?: string; $left?: string; $right?: string; $bottom?: string; $delay?: number; $color: string }>`
  position: absolute;
  top: ${p => p.$top ?? 'auto'}; left: ${p => p.$left ?? 'auto'};
  right: ${p => p.$right ?? 'auto'}; bottom: ${p => p.$bottom ?? 'auto'};
  width: 52px; height: 52px; border-radius: 14px;
  background: rgba(255,255,255,0.88); backdrop-filter: blur(8px);
  display: flex; align-items: center; justify-content: center;
  box-shadow: 0 4px 14px rgba(0,0,0,0.12);
  animation: ${p => (p.$delay ?? 0) % 2 === 0 ? float : floatDelay} 3.5s ease-in-out ${p => p.$delay ?? 0}s infinite;
  svg { width: 32px; height: 32px; fill: ${p => p.$color}; }
`;
const CharCard = styled.div`
  width: min(50vw, 180px); aspect-ratio: 1;
  display: flex; align-items: center; justify-content: center;
  animation: ${bounce} 2s ease-in-out infinite;
  filter: drop-shadow(0 12px 28px rgba(0,0,0,0.15));
  svg { width: 100%; height: 100%; fill: ${PRIMARY}; }
`;
const InfoCard = styled.div`
  background: #fff; border-radius: 32px 32px 0 0;
  padding: 24px 24px max(32px, env(safe-area-inset-bottom));
  box-shadow: 0 -8px 32px rgba(0,0,0,0.08);
  animation: ${fadeInUp} 0.6s ease; font-family: ${FONT};
`;
const Handle = styled.div`width: 40px; height: 4px; background: #e0e0e0; border-radius: 99px; margin: 0 auto 18px;`;
const IntroTitle = styled.h1`
  font-size: clamp(26px, 7vw, 32px); font-weight: 900; margin: 0 0 8px; color: #1a2a2a;
  span { color: ${PRIMARY}; }
`;
const IntroDesc = styled.p`font-size: 14px; color: #7a8a8a; line-height: 1.6; margin: 0 0 20px;`;
const StartBtn = styled.button`
  width: 100%; padding: 16px 24px; border-radius: 99px; border: none;
  background: ${PRIMARY_GR}; color: #fff; font-size: 17px; font-weight: 800;
  cursor: pointer; box-shadow: 0 6px 24px rgba(43,188,175,0.4); font-family: ${FONT};
  transition: all 0.2s;
  &:hover { transform: translateY(-2px); }
  &:active { transform: translateY(0); }
`;

// ─── Game Styled Components ───────────────────────────────────────────────────
const GameWrap = styled.div`
  min-height: 100dvh; display: flex; flex-direction: column;
  background: ${BG_GR}; font-family: ${FONT};
  user-select: none;
`;
const GameNav = styled.header`
  position: sticky; top: 0; z-index: 10;
  background: rgba(255,255,255,0.92); backdrop-filter: blur(12px);
  border-bottom: 1px solid rgba(45,188,175,0.12);
  padding: 10px 16px; display: flex; align-items: center; justify-content: space-between;
`;
const NavLeft = styled.div`display: flex; align-items: center; gap: 8px;`;
const GameTitle = styled.span`font-size: 17px; font-weight: 800; color: #1a2a2a;`;
const ScoreBadge = styled.div`
  background: rgba(45,188,175,0.12); color: ${PRIMARY};
  padding: 6px 14px; border-radius: 10px; font-size: 14px; font-weight: 800;
`;
const GameBody = styled.div`
  flex: 1; display: flex; flex-direction: column;
  align-items: center; padding: 16px; gap: 16px;
  max-width: 520px; margin: 0 auto; width: 100%;
`;
const LevelInfo = styled.div`
  font-size: 13px; color: #7a8a8a; text-align: center; font-weight: 600;
`;
const Instruction = styled.div`
  font-size: 15px; color: #1a2a2a; text-align: center; font-weight: 600;
  background: rgba(255,255,255,0.8); padding: 10px 20px; border-radius: 12px;
`;
const GameArea = styled.div`
  position: relative; width: 100%; aspect-ratio: 1;
  background: #fff; border-radius: 24px;
  box-shadow: 0 4px 24px rgba(0,0,0,0.08);
  overflow: hidden; touch-action: none;
`;
const SVGOverlay = styled.svg`
  position: absolute; inset: 0; width: 100%; height: 100%;
  pointer-events: none; z-index: 5;
`;
const ConnectionLine = styled.line`
  stroke: ${PRIMARY}; stroke-width: 4; stroke-linecap: round;
  filter: drop-shadow(0 2px 4px rgba(45,188,175,0.3));
`;
const DragLine = styled.line`
  stroke: ${PRIMARY}; stroke-width: 4; stroke-linecap: round;
  stroke-dasharray: 8 8; opacity: 0.7;
`;
const ShapeItem = styled.div<{ $x: number; $y: number; $dragging: boolean; $matched: boolean; $shake: boolean; $hovered: boolean }>`
  position: absolute;
  left: ${p => p.$x}%; top: ${p => p.$y}%;
  transform: translate(-50%, -50%) ${p => p.$dragging ? 'scale(1.15)' : p.$hovered ? 'scale(1.1)' : 'scale(1)'};
  width: 64px; height: 64px;
  background: #fff; 
  border: 3px solid ${p => p.$dragging ? PRIMARY : p.$matched ? '#22c55e' : p.$hovered ? PRIMARY : '#e2e8f0'};
  border-radius: 16px; 
  cursor: ${p => p.$matched ? 'default' : 'grab'};
  display: flex; align-items: center; justify-content: center;
  transition: ${p => p.$dragging ? 'none' : 'all 0.2s'}; 
  z-index: ${p => p.$dragging ? 100 : 10};
  box-shadow: ${p => p.$dragging 
    ? '0 8px 32px rgba(45,188,175,0.5)' 
    : p.$hovered 
      ? '0 4px 20px rgba(45,188,175,0.4)' 
      : '0 2px 8px rgba(0,0,0,0.1)'};
  ${p => p.$dragging && css`animation: ${pulseGlow} 0.5s ease-in-out infinite;`}
  ${p => p.$shake && css`animation: ${shakeAnim} 0.5s ease;`}
  ${p => p.$matched && css`
    animation: ${popIn} 0.3s ease;
    opacity: 0.5;
    pointer-events: none;
  `}
  svg { width: 40px; height: 40px; pointer-events: none; }
`;

// ─── Result Modal ─────────────────────────────────────────────────────────────
const ResultOverlay = styled.div`
  position: fixed; inset: 0; z-index: 9999;
  background: rgba(0,0,0,0.5); backdrop-filter: blur(4px);
  display: flex; align-items: center; justify-content: center;
  padding: 24px;
`;
const ResultCard = styled.div`
  background: #fff; border-radius: 28px; padding: 32px;
  text-align: center; max-width: 340px; width: 100%;
  animation: ${fadeInUp} 0.4s ease;
`;
const ResultEmoji = styled.div`font-size: 64px; margin-bottom: 16px;`;
const ResultTitle = styled.h2`font-size: 24px; font-weight: 900; color: #1a2a2a; margin: 0 0 8px;`;
const ResultDesc = styled.p`font-size: 14px; color: #7a8a8a; margin: 0 0 24px;`;
const ResultBtn = styled.button<{ $primary?: boolean }>`
  width: 100%; padding: 14px; border-radius: 99px; border: none;
  background: ${p => p.$primary ? PRIMARY_GR : '#f1f5f9'};
  color: ${p => p.$primary ? '#fff' : '#64748b'};
  font-size: 16px; font-weight: 700; cursor: pointer;
  font-family: ${FONT}; margin-bottom: 10px;
  transition: all 0.15s;
  &:hover { transform: translateY(-2px); }
  &:active { transform: scale(0.97); }
  &:last-child { margin-bottom: 0; }
`;

// ─── Utility Functions ────────────────────────────────────────────────────────
function shuffle<T>(arr: T[]): T[] {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

function generatePositions(count: number): { x: number; y: number }[] {
  const positions: { x: number; y: number }[] = [];
  const minDist = 20;
  const padding = 15;
  let attempts = 0;
  
  while (positions.length < count && attempts < 500) {
    const x = padding + Math.random() * (100 - 2 * padding);
    const y = padding + Math.random() * (100 - 2 * padding);
    
    const tooClose = positions.some(p => 
      Math.sqrt((p.x - x) ** 2 + (p.y - y) ** 2) < minDist
    );
    
    if (!tooClose) {
      positions.push({ x, y });
    }
    attempts++;
  }
  
  while (positions.length < count) {
    const idx = positions.length;
    const cols = Math.ceil(Math.sqrt(count));
    const row = Math.floor(idx / cols);
    const col = idx % cols;
    positions.push({
      x: 20 + (col * 60 / cols),
      y: 20 + (row * 60 / cols),
    });
  }
  
  return positions;
}

// ─── Main Component ───────────────────────────────────────────────────────────
export default function ShapeMatchGame() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const gameAreaRef = useRef<HTMLDivElement>(null);
  
  const [phase, setPhase] = useState<'intro' | 'playing' | 'levelComplete' | 'gameComplete'>('intro');
  const [level, setLevel] = useState(0);
  const [score, setScore] = useState(0);
  const [shapes, setShapes] = useState<PlacedShape[]>([]);
  const [connections, setConnections] = useState<Connection[]>([]);
  const [drag, setDrag] = useState<DragState>({
    active: false,
    fromShape: null,
    startX: 0,
    startY: 0,
    currentX: 0,
    currentY: 0,
  });
  const [hoveredShapeId, setHoveredShapeId] = useState<string | null>(null);
  const [shakeId, setShakeId] = useState<string | null>(null);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showPremiumModal, setShowPremiumModal] = useState(false);
  const [gameSettings, setGameSettings] = useState<{ loginLevel: number; premiumLevel: number }>({
    loginLevel: DEFAULT_LOGIN_START,
    premiumLevel: DEFAULT_PREMIUM_START,
  });
  
  const [savedProgress, setSavedProgress] = useState<{ maxLevel: number; lastScore: number } | null>(null);

  // Fetch game settings and progress from API
  useEffect(() => {
    // Fetch game settings
    fetch('/api/games/shape-match')
      .then(res => res.json())
      .then(data => {
        if (data.loginRequiredLevel || data.premiumStartLevel) {
          setGameSettings({
            loginLevel: data.loginRequiredLevel || DEFAULT_LOGIN_START,
            premiumLevel: data.premiumStartLevel || DEFAULT_PREMIUM_START,
          });
        }
      })
      .catch(() => {});
    
    // Fetch saved progress (if logged in)
    if (user) {
      fetch('/api/progress/shape-match', { credentials: 'include' })
        .then(res => res.json())
        .then(data => {
          if (data.ok && data.progress) {
            setSavedProgress(data.progress);
          }
        })
        .catch(() => {});
    }
  }, [user]);

  // Save progress when level changes
  const saveProgress = useCallback((completedLevel: number, currentScore: number) => {
    if (!user) return;
    
    fetch('/api/progress/shape-match', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ level: completedLevel, score: currentScore }),
    }).catch(() => {});
  }, [user]);
  
  // Check if user has active premium
  const isPremium = user?.subscription?.status === 'active' && 
    user?.subscription?.endDate && 
    new Date(user.subscription.endDate) > new Date();

  // Generate shapes for current level
  const generateLevel = useCallback((lvl: number) => {
    const config = LEVELS[lvl];
    const selectedShapes = shuffle(SHAPES).slice(0, config.shapeCount);
    const positions = generatePositions(config.shapeCount * 2);
    const baseColor = COLORS[Math.floor(Math.random() * COLORS.length)];
    
    const placed: PlacedShape[] = [];
    
    selectedShapes.forEach((shape, idx) => {
      const color = config.sameColor ? baseColor : COLORS[idx % COLORS.length];
      const pairId = `pair-${idx}`;
      
      placed.push({
        id: `${shape.id}-a-${idx}`,
        shapeType: shape,
        color,
        x: positions[idx * 2].x,
        y: positions[idx * 2].y,
        pairId,
        matched: false,
      });
      
      placed.push({
        id: `${shape.id}-b-${idx}`,
        shapeType: shape,
        color,
        x: positions[idx * 2 + 1].x,
        y: positions[idx * 2 + 1].y,
        pairId,
        matched: false,
      });
    });
    
    setShapes(shuffle(placed));
    setConnections([]);
    setDrag({ active: false, fromShape: null, startX: 0, startY: 0, currentX: 0, currentY: 0 });
    setHoveredShapeId(null);
  }, []);

  const startGame = () => {
    setLevel(0);
    setScore(0);
    generateLevel(0);
    setPhase('playing');
  };

  const handleNextLevel = () => {
    const next = level + 1;
    const nextLevelNum = next + 1; // Convert to 1-based level number
    
    // Check login requirement (e.g., Level 2+ requires login)
    if (nextLevelNum >= gameSettings.loginLevel && !user) {
      setShowLoginModal(true);
      return;
    }
    
    // Check premium requirement (e.g., Level 6+ requires premium)
    if (nextLevelNum >= gameSettings.premiumLevel && !isPremium) {
      setShowPremiumModal(true);
      return;
    }
    
    if (next >= MAX_LEVELS) {
      playClap();
      setPhase('gameComplete');
    } else {
      // Close modal first, then setup new level
      setPhase('playing');
      setLevel(next);
      // Small delay to ensure phase change renders first
      setTimeout(() => generateLevel(next), 50);
    }
  };
  
  const handleGoHome = () => {
    navigate('/');
  };
  
  const handlePlayAgain = () => {
    setPhase('playing');
    setLevel(0);
    setScore(0);
    setTimeout(() => generateLevel(0), 50);
  };

  // Get coordinates relative to game area
  const getRelativeCoords = useCallback((clientX: number, clientY: number) => {
    if (!gameAreaRef.current) return { x: 0, y: 0 };
    const rect = gameAreaRef.current.getBoundingClientRect();
    return {
      x: ((clientX - rect.left) / rect.width) * 100,
      y: ((clientY - rect.top) / rect.height) * 100,
    };
  }, []);

  // Find shape at position
  const findShapeAtPosition = useCallback((x: number, y: number, excludeId?: string): PlacedShape | null => {
    const hitRadius = 12;
    return shapes.find(s => 
      !s.matched && 
      s.id !== excludeId &&
      Math.abs(s.x - x) < hitRadius && 
      Math.abs(s.y - y) < hitRadius
    ) || null;
  }, [shapes]);

  // Mouse/Touch handlers
  const handlePointerDown = useCallback((e: React.PointerEvent, shape: PlacedShape) => {
    if (shape.matched || phase !== 'playing') return;
    e.preventDefault();
    
    const coords = getRelativeCoords(e.clientX, e.clientY);
    setDrag({
      active: true,
      fromShape: shape,
      startX: shape.x,
      startY: shape.y,
      currentX: coords.x,
      currentY: coords.y,
    });
  }, [getRelativeCoords, phase]);

  const handlePointerMove = useCallback((e: React.PointerEvent) => {
    if (!drag.active || phase !== 'playing') return;
    
    const coords = getRelativeCoords(e.clientX, e.clientY);
    setDrag(prev => ({ ...prev, currentX: coords.x, currentY: coords.y }));
    
    // Check if hovering over a valid target
    const target = findShapeAtPosition(coords.x, coords.y, drag.fromShape?.id);
    setHoveredShapeId(target?.id || null);
  }, [drag.active, drag.fromShape, getRelativeCoords, findShapeAtPosition, phase]);

  const handlePointerUp = useCallback((e: React.PointerEvent) => {
    if (phase !== 'playing') return;
    if (!drag.active || !drag.fromShape) {
      setDrag({ active: false, fromShape: null, startX: 0, startY: 0, currentX: 0, currentY: 0 });
      return;
    }
    
    const coords = getRelativeCoords(e.clientX, e.clientY);
    const targetShape = findShapeAtPosition(coords.x, coords.y, drag.fromShape.id);
    
    if (targetShape) {
      if (targetShape.pairId === drag.fromShape.pairId) {
        // Match!
        playCorrect();
        setScore(s => s + 10);
        
        const newConn: Connection = { from: drag.fromShape, to: targetShape };
        setConnections(prev => [...prev, newConn]);
        
        setShapes(prev => prev.map(s => 
          s.pairId === targetShape.pairId ? { ...s, matched: true } : s
        ));
        
        // Check level complete
        setTimeout(() => {
          const remaining = shapes.filter(s => !s.matched && s.pairId !== targetShape.pairId);
          if (remaining.length === 0) {
            playClap();
            // Save progress (level is 0-indexed, so level+1 is the completed level number)
            saveProgress(level + 1, score + 10);
            setPhase('levelComplete');
          }
        }, 100);
      } else {
        // Wrong match
        playWrong();
        setShakeId(targetShape.id);
        setTimeout(() => setShakeId(null), 500);
      }
    }
    
    setDrag({ active: false, fromShape: null, startX: 0, startY: 0, currentX: 0, currentY: 0 });
    setHoveredShapeId(null);
  }, [drag, shapes, getRelativeCoords, findShapeAtPosition, phase]);

  // Global pointer move/up for when pointer leaves shape - only for game area
  useEffect(() => {
    if (phase !== 'playing') return; // Don't add listeners when not playing
    
    const handleGlobalMove = (e: PointerEvent) => {
      if (!drag.active || !gameAreaRef.current) return;
      const coords = getRelativeCoords(e.clientX, e.clientY);
      setDrag(prev => ({ ...prev, currentX: coords.x, currentY: coords.y }));
      const target = findShapeAtPosition(coords.x, coords.y, drag.fromShape?.id);
      setHoveredShapeId(target?.id || null);
    };
    
    const handleGlobalUp = (e: PointerEvent) => {
      // Only handle if we're actively dragging in the game
      if (!drag.active) return;
      setDrag({ active: false, fromShape: null, startX: 0, startY: 0, currentX: 0, currentY: 0 });
      setHoveredShapeId(null);
    };
    
    window.addEventListener('pointermove', handleGlobalMove);
    window.addEventListener('pointerup', handleGlobalUp);
    return () => {
      window.removeEventListener('pointermove', handleGlobalMove);
      window.removeEventListener('pointerup', handleGlobalUp);
    };
  }, [phase, drag.active, drag.fromShape, getRelativeCoords, findShapeAtPosition]);

  // ─── Render ─────────────────────────────────────────────────────────────────
  
  if (phase === 'intro') {
    return (
      <IntroOverlay>
        <IntroWrapper>
          <TopNav>
            <BackBtn onClick={() => navigate('/')}>
              <span className="material-symbols-outlined">arrow_back</span>
            </BackBtn>
          </TopNav>
          
          <CharArea>
            <FloatShape $top="15%" $left="10%" $delay={0} $color="#3B82F6">
              <svg viewBox={SHAPES[0].viewBox}><path d={SHAPES[0].path} /></svg>
            </FloatShape>
            <FloatShape $top="20%" $right="12%" $delay={1} $color="#EF4444">
              <svg viewBox={SHAPES[1].viewBox}><path d={SHAPES[1].path} /></svg>
            </FloatShape>
            <FloatShape $bottom="25%" $left="15%" $delay={2} $color="#22C55E">
              <svg viewBox={SHAPES[2].viewBox}><path d={SHAPES[2].path} /></svg>
            </FloatShape>
            <FloatShape $bottom="20%" $right="10%" $delay={3} $color="#F59E0B">
              <svg viewBox={SHAPES[3].viewBox}><path d={SHAPES[3].path} /></svg>
            </FloatShape>
            
            <CharCard>
              <svg viewBox={SHAPES[2].viewBox}><path d={SHAPES[2].path} /></svg>
            </CharCard>
          </CharArea>
          
          <InfoCard>
            <Handle />
            <IntroTitle>Hubungkan <span>Bentuk!</span></IntroTitle>
            <IntroDesc>
              Tarik garis dari satu bentuk ke bentuk yang sama untuk menghubungkannya! 🎨
            </IntroDesc>
            {savedProgress && savedProgress.maxLevel > 0 && (
              <IntroDesc style={{ color: '#2DBCAF', fontWeight: 600 }}>
                📊 Progress tersimpan: Level {savedProgress.maxLevel} • Skor: {savedProgress.lastScore}
              </IntroDesc>
            )}
            <StartBtn onClick={startGame}>
              {savedProgress && savedProgress.maxLevel > 0 ? 'Main dari Awal' : 'Mulai Main'}
            </StartBtn>
            {savedProgress && savedProgress.maxLevel > 0 && savedProgress.maxLevel < MAX_LEVELS && (
              <StartBtn 
                onClick={() => {
                  const continueLevel = savedProgress.maxLevel; // 0-indexed
                  const continueLevelNum = continueLevel + 1;   // 1-based for display/check
                  
                  // Check login requirement
                  if (continueLevelNum >= gameSettings.loginLevel && !user) {
                    setShowLoginModal(true);
                    return;
                  }
                  
                  // Check premium requirement
                  if (continueLevelNum >= gameSettings.premiumLevel && !isPremium) {
                    setShowPremiumModal(true);
                    return;
                  }
                  
                  setLevel(continueLevel);
                  setScore(savedProgress.lastScore);
                  generateLevel(continueLevel);
                  setPhase('playing');
                }}
                style={{ marginTop: 10, background: '#f1f5f9', color: '#475569', boxShadow: 'none' }}
              >
                Lanjutkan Level {savedProgress.maxLevel + 1}
              </StartBtn>
            )}
          </InfoCard>
        </IntroWrapper>
        
        {/* Modals for intro screen */}
        {showLoginModal && (
          <LoginPromptModal
            onLogin={() => {
              setShowLoginModal(false);
              navigate('/profile');
            }}
            onClose={() => setShowLoginModal(false)}
            subtitle={<>Login untuk melanjutkan ke level berikutnya dan simpan progresmu!</>}
          />
        )}
        
        {showPremiumModal && (
          <PremiumUpgradeModal
            isOpen={showPremiumModal}
            onClose={() => setShowPremiumModal(false)}
            featureName="Level Premium"
          />
        )}
      </IntroOverlay>
    );
  }

  return (
    <GameWrap>
      <GameNav>
        <NavLeft>
          <BackBtn onClick={() => navigate('/')}>
            <span className="material-symbols-outlined">arrow_back</span>
          </BackBtn>
          <GameTitle>Hubungkan Bentuk</GameTitle>
        </NavLeft>
        <ScoreBadge>⭐ {score}</ScoreBadge>
      </GameNav>
      
      <GameBody>
        <LevelInfo>Level {level + 1} dari {MAX_LEVELS}</LevelInfo>
        <Instruction>👆 Tarik garis untuk menghubungkan bentuk yang sama!</Instruction>
        
        <GameArea 
          ref={gameAreaRef}
          onPointerMove={phase === 'playing' ? handlePointerMove : undefined}
          onPointerUp={phase === 'playing' ? handlePointerUp : undefined}
        >
          <SVGOverlay viewBox="0 0 100 100" preserveAspectRatio="none">
            {/* Completed connections */}
            {connections.map((conn, i) => (
              <ConnectionLine
                key={i}
                x1={conn.from.x}
                y1={conn.from.y}
                x2={conn.to.x}
                y2={conn.to.y}
              />
            ))}
            {/* Active drag line */}
            {drag.active && drag.fromShape && (
              <DragLine
                x1={drag.fromShape.x}
                y1={drag.fromShape.y}
                x2={drag.currentX}
                y2={drag.currentY}
              />
            )}
          </SVGOverlay>
          
          {shapes.map(shape => (
            <ShapeItem
              key={shape.id}
              $x={shape.x}
              $y={shape.y}
              $dragging={drag.fromShape?.id === shape.id}
              $matched={shape.matched}
              $shake={shakeId === shape.id}
              $hovered={hoveredShapeId === shape.id}
              onPointerDown={(e) => handlePointerDown(e, shape)}
            >
              <svg viewBox={shape.shapeType.viewBox}>
                <path d={shape.shapeType.path} fill={shape.color} />
              </svg>
            </ShapeItem>
          ))}
        </GameArea>
      </GameBody>
      
      {phase === 'levelComplete' && !showLoginModal && !showPremiumModal && (
        <ResultOverlay>
          <ResultCard>
            <ResultEmoji>🎉</ResultEmoji>
            <ResultTitle>Level {level + 1} Selesai!</ResultTitle>
            <ResultDesc>Keren! Kamu berhasil menghubungkan semua bentuk.</ResultDesc>
            <ResultBtn $primary onClick={handleNextLevel}>
              {level + 1 >= MAX_LEVELS ? 'Lihat Hasil' : 'Level Berikutnya'}
            </ResultBtn>
            <ResultBtn onClick={handleGoHome}>Kembali ke Menu</ResultBtn>
          </ResultCard>
        </ResultOverlay>
      )}
      
      {phase === 'gameComplete' && (
        <ResultOverlay>
          <ResultCard>
            <ResultEmoji>🏆</ResultEmoji>
            <ResultTitle>Luar Biasa!</ResultTitle>
            <ResultDesc>Kamu menyelesaikan semua level dengan skor {score} poin!</ResultDesc>
            <ResultBtn $primary onClick={handlePlayAgain}>Main Lagi</ResultBtn>
            <ResultBtn onClick={handleGoHome}>Kembali ke Menu</ResultBtn>
          </ResultCard>
        </ResultOverlay>
      )}
      
      {showLoginModal && (
        <LoginPromptModal
          onLogin={() => {
            setShowLoginModal(false);
            navigate('/profile');
          }}
          onClose={() => setShowLoginModal(false)}
          subtitle={<>Login untuk melanjutkan ke level berikutnya dan simpan progresmu!</>}
        />
      )}
      
      {showPremiumModal && (
        <PremiumUpgradeModal
          isOpen={showPremiumModal}
          onClose={() => setShowPremiumModal(false)}
          featureName="Level 6-10"
        />
      )}
    </GameWrap>
  );
}
